/**
 * 三康看板的视图模型层（**纯函数，不依赖 DOM，可在 Node 里直接测**）。
 *
 * 为什么要有这一层：
 *   原生小程序渲染在本机无法验证，因此本项目的做法是把「可验证的那一半」抽出来 ——
 *   请求层 / 数据映射 / 格式化 / 状态机全部放进不依赖 DOM 的纯模块，让 `node` 直接跑断言。
 *   本文件只做一件事：**接口响应 → 视图数据**，不做请求、不读全局状态、不碰 window。
 *
 * 三条硬约定（决定了下面所有分支的写法）：
 *   1. **不写死业务数字**。分值、λ、权重、增益全部来自 `/api/sk/*`；这里只做搬运与格式化。
 *      连 0.40 / 0.30 / 0.30 这种权重也不在前端出现 —— 权重从 `engine.weights` 读。
 *   2. **不编造数字**。接口少给一项时，返回 `available: false` + `missing: [...]` +
 *      展示文案「缺失」，而**不是** 0、不是中位数、不是上一次的缓存值。0 是合法分值，
 *      用 0 冒充缺失会让成员以为自己的企业治理健康掉到了谷底。
 *   3. **接口给出的顺序优先**。`/api/sk/leverage` 已按增益降序返回；只有万一不是降序时
 *      才自行排序，并在结果里标记 `reordered: true`（不掩盖「服务端给的顺序与约定不符」）。
 *
 * 字段来源：`apps/api/src/modules/sk.routes.ts` 与 `apps/api/src/core/skEngine.ts`。
 */

/** 三康三轴。与 `skEngine.AXES` 一致；键上带 `undefined` 是因为接口可能少给一项。 */
export type SkAxisKey = 'H' | 'E' | 'W';

/** 轴键 → 语义色（H 绿 / E 蓝 / W 金），与全站 `resolveColor('h'|'e'|'w')` 对应。 */
type ToneKey = 'h' | 'e' | 'w';

/** 缺失数据的统一展示文案：任何取不到的数字都显示它，绝不显示 0。 */
export const MISSING = '缺失';

/** 未标定的统一标注文案。接口返回 `calibrated: false` 时原样带出，不美化成「已校准」。 */
export const UNCALIBRATED = '未校准';

/* ------------------------------------------------------------------ *
 * 一、接口响应形状（以 sk.routes.ts 的返回值为准）
 * ------------------------------------------------------------------ */

/** 可选布尔/数字：接口可能少给字段，因此类型上就允许 undefined，避免用断言掩盖。 */
export interface SkEngineState {
  key?: string;
  label?: string;
  tone?: string;
  desc?: string;
}

export interface SkCoupling {
  from?: string;
  to?: string;
  g?: number;
  lag?: string;
  sign?: number;
  name?: string;
  path?: string;
}

export interface SkEngineResponse {
  axes?: string[];
  lambda?: number;
  state?: SkEngineState;
  /** 几何平均的指数即权重，例如 { H: 0.4, E: 0.3, W: 0.3 } —— 由服务端下发 */
  weights?: { H?: number; E?: number; W?: number };
  param?: { alpha?: number; gamma?: number; beta?: number; months?: number };
  couplings?: SkCoupling[];
  calibrated?: boolean;
  notice?: string;
}

export interface SkScoreAxis {
  key?: string;
  name?: string;
  value?: number;
  weight?: number;
  grade?: string;
  gradeText?: string;
}

export interface SkScoreResponse {
  composite?: number;
  grade?: string;
  gradeText?: string;
  axes?: SkScoreAxis[];
  formula?: string;
  note?: string;
  scale?: { dims?: number; items?: number; breakdown?: Record<string, number> };
  source?: string;
  measuredAt?: string;
}

export interface SkSimulateRow {
  /** 37 个点：初始 + 36 个月，每点 [H, E, W] */
  hist?: number[][];
  final?: number[];
  composite?: number;
  minima?: { v?: number; m?: number }[];
  /** 首次三维同时回到个体化基准的月份；-1 表示窗口内未回稳 */
  recovery?: number;
}

export interface SkSimulateResponse {
  input?: { x?: number[]; xStar?: number[]; u?: number[]; shock?: number[]; months?: number };
  withIntervention?: SkSimulateRow;
  withoutIntervention?: SkSimulateRow;
  deviation?: { withIntervention?: number[][]; withoutIntervention?: number[][] };
  lambda?: number;
  state?: SkEngineState;
  baselineSource?: string;
  calibrated?: boolean;
  notice?: string;
}

export interface SkLeverageApiRow {
  key?: string;
  name?: string;
  /** 同等干预强度下，36 个月后综合指数的边际提升 */
  gain?: number;
  finalComposite?: number;
  absorption?: number;
  ratio?: number;
}

export interface SkLeverageResponse {
  input?: { x?: number[]; xStar?: number[]; intensity?: number };
  base?: { composite?: number; final?: number };
  rows?: SkLeverageApiRow[];
  conclusion?: string;
  calibrated?: boolean;
}

/* ------------------------------------------------------------------ *
 * 二、视图模型（页面只渲染这些结构；其中所有数字都直接来自接口）
 * ------------------------------------------------------------------ */

/** 三轴视图项的公共部分：`available` 为 false 时 `display` 必为「缺失」。 */
export interface SkAxisView {
  key: SkAxisKey | undefined;
  /** 接口给的中文名；缺失时为空串 */
  name: string;
  tone: ToneKey;
  value: number | undefined;
  display: string;
}

export interface SkCompositeView {
  available: boolean;
  missing: string[];
  composite: number | undefined;
  compositeDisplay: string;
  grade: string | undefined;
  gradeText: string | undefined;
  formula: string;
  note: string;
  axes: (SkAxisView & { grade: string | undefined; gradeText: string | undefined; weight: number | undefined; weightDisplay: string })[];
  measuredAt: string;
  source: string;
}

export interface SkSpiralView {
  available: boolean;
  missing: string[];
  lambda: number | undefined;
  lambdaDisplay: string;
  stateKey: string | undefined;
  label: string;
  desc: string;
  tone: string;
  /** 是否处于放大偏离的状态（λ > 1）。未拿到 λ 时为 undefined —— 不猜。 */
  amplifying: boolean | undefined;
  calibrated: boolean | undefined;
  calibratedText: string;
  calibratedTone: 'warn' | 'ok' | 'ghost';
  notice: string;
}

export interface SkLeverageRowView {
  key: SkAxisKey | undefined;
  name: string;
  tone: ToneKey;
  order: number;
  gain: number | undefined;
  gainDisplay: string;
  finalComposite: number | undefined;
  absorption: number | undefined;
  absorptionDisplay: string;
  ratio: number | undefined;
}

export interface SkLeverageView {
  available: boolean;
  /** 接口返回的顺序**不是**按增益降序，前端已自行排序并如实标记 */
  reordered: boolean;
  rows: SkLeverageRowView[];
  conclusion: string;
  calibrated: boolean | undefined;
}

export interface SkChannelView {
  from: string;
  to: string;
  fromName: string;
  toName: string;
  /** 强度（弹性系数）；接口未给时为 undefined，展示「缺失」 */
  g: number | undefined;
  gDisplay: string;
  lag: string;
  path: string;
  name: string;
}

export interface SkSimulationAxisView {
  key: SkAxisKey;
  name: string;
  tone: ToneKey;
  final: number | undefined;
  finalDisplay: string;
  /** 轨迹最低点与其发生月份 */
  minimum: number | undefined;
  minimumMonth: number | undefined;
  /** 该轴在窗口内是否回到个体化基准 */
  recovered: boolean | undefined;
  /** 37 个点的轨迹（初始 + 窗口内各月），来自接口 hist */
  trajectory: number[];
}

export interface SkSimulationView {
  available: boolean;
  missing: string[];
  /** 推演窗口（月），来自接口 input.months */
  months: number | undefined;
  /** 窗口末月该轴是否回到基线；-1 / 缺失 → false */
  recovered: boolean;
  recovery: number | undefined;
  /** 未在窗口内回到基线的轴 */
  unrecoveredKeys: string[];
  withComposite: number | undefined;
  withCompositeDisplay: string;
  withoutComposite: number | undefined;
  withoutCompositeDisplay: string;
  /** 不干预轨迹末月综合指数 */
  withoutFinal: number | undefined;
  axes: SkSimulationAxisView[];
  lambda: number | undefined;
  baselineSource: string;
  calibrated: boolean | undefined;
  calibratedText: string;
  notice: string;
  /** 情景推演而非预测：标注为常量，由页面直接呈现 */
  disclaimer: string;
  /** 接口未提供推演结果时的原因文案 */
  reason: string;
}

/* ------------------------------------------------------------------ *
 * 三、取值与格式化小工具
 * ------------------------------------------------------------------ */

const AXIS_KEYS: readonly SkAxisKey[] = ['H', 'E', 'W'];

const TONES: Record<SkAxisKey, ToneKey> = { H: 'h', E: 'e', W: 'w' };

/**
 * 判定是否为可用数字。
 * 用 `Number.isFinite` 而不是 `typeof === 'number'`：NaN 与 Infinity 不能被当成有效分值。
 */
function hasNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** 数字 → 展示文案。缺失时返回「缺失」，绝不用 0 冒充。 */
export function formatNumber(value: unknown, digits = 1): string {
  return hasNumber(value) ? value.toFixed(digits) : MISSING;
}

/** 取值：非有限数字一律视为缺失（NaN / undefined / 字符串都不会被当成数字用）。 */
function num(value: unknown): number | undefined {
  return hasNumber(value) ? value : undefined;
}

function isAxisKey(value: unknown): value is SkAxisKey {
  return value === 'H' || value === 'E' || value === 'W';
}

function asArray<T>(value: T[] | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function asMatrix(value: number[][] | undefined): number[][] {
  return Array.isArray(value) ? value : [];
}

/** 轴名：优先接口给的中文名；接口未给时回落到口径常量（文案，不是业务数字）。 */
function axisName(raw: SkAxisApiAxis | undefined, key: SkAxisKey, names: AxisNameTable): string {
  if (raw && typeof raw.name === 'string' && raw.name.length > 0) return raw.name;
  return names[key];
}

/** 取某轴在接口 `axes` 数组里的原始项。 */
interface SkAxisApiAxis {
  key?: string;
  value?: number;
  weight?: number;
  grade?: string;
  gradeText?: string;
  name?: string;
}

function pickAxis(axes: SkAxisApiAxis[] | undefined, key: SkAxisKey): SkAxisApiAxis | undefined {
  return asArray(axes).find((a) => a?.key === key);
}

/** 逐轴名称表（口径常量，非业务数字）。 */
export interface AxisNameTable {
  H: string;
  E: string;
  W: string;
}

/* ------------------------------------------------------------------ *
 * 四、四个映射函数
 * ------------------------------------------------------------------ */

/** 综合指数卡：数值 + 等级 + 三轴分值与权重（权重来自接口，前端不写 40/30/30）。 */
export function toCompositeView(score: SkScoreResponse | undefined, names: AxisNameTable): SkCompositeView {
  const s = score ?? {};
  const axesRaw = asArray(s.axes);
  const missing: string[] = [];

  if (!hasNumber(s.composite)) missing.push('composite');
  if (typeof s.grade !== 'string' || s.grade.length === 0) missing.push('grade');

  const axes = AXIS_KEYS.map((key) => {
    const raw = pickAxis(axesRaw, key);
    if (!raw) missing.push(`axes.${key}`);
    else if (!hasNumber(raw.value)) missing.push(`axes.${key}.value`);
    const value = num(raw?.value);
    const weight = num(raw?.weight);
    return {
      key,
      name: axisName(raw, key, names),
      tone: TONES[key],
      value,
      display: formatNumber(value),
      grade: typeof raw?.grade === 'string' && raw.grade.length > 0 ? raw.grade : undefined,
      gradeText: typeof raw?.gradeText === 'string' && raw.gradeText.length > 0 ? raw.gradeText : undefined,
      weight,
      // 权重是接口下发的几何平均指数；缺失时同样显示「缺失」，不写死 0.4/0.3/0.3
      weightDisplay: weight === undefined ? MISSING : weight.toFixed(2),
    };
  });

  return {
    available: missing.length === 0,
    missing,
    composite: num(s.composite),
    compositeDisplay: formatNumber(s.composite),
    grade: typeof s.grade === 'string' && s.grade.length > 0 ? s.grade : undefined,
    gradeText: typeof s.gradeText === 'string' && s.gradeText.length > 0 ? s.gradeText : undefined,
    formula: typeof s.formula === 'string' ? s.formula : '',
    note: typeof s.note === 'string' ? s.note : '',
    axes,
    measuredAt: typeof s.measuredAt === 'string' ? s.measuredAt : '',
    source: typeof s.source === 'string' ? s.source : '',
  };
}

/**
 * 螺旋状态：λ 与状态名（强发散 / 弱发散 / 临界 / 收敛，以接口返回为准）。
 *
 * 「λ > 1 表示偏离会被放大、不干预不会自愈」是产品的核心判断，
 * 因此 `amplifying` 必须由 λ 与 1 的比较得出；拿不到 λ 时留 undefined，不替成员下结论。
 */
export function toSpiralView(engine: SkEngineResponse | undefined): SkSpiralView {
  const e = engine ?? {};
  const lambda = num(e.lambda);
  const stateLabel = typeof e.state?.label === 'string' && e.state.label.length > 0 ? e.state.label : '';
  const missing: string[] = [];
  if (lambda === undefined) missing.push('lambda');
  if (stateLabel.length === 0) missing.push('state');

  const calibrated = typeof e.calibrated === 'boolean' ? e.calibrated : undefined;

  return {
    available: missing.length === 0,
    missing,
    lambda,
    lambdaDisplay: lambda === undefined ? MISSING : lambda.toFixed(4),
    stateKey: typeof e.state?.key === 'string' ? e.state.key : undefined,
    label: stateLabel.length > 0 ? stateLabel : MISSING,
    desc: typeof e.state?.desc === 'string' ? e.state.desc : '',
    tone: typeof e.state?.tone === 'string' && e.state.tone.length > 0 ? e.state.tone : 'ghost',
    amplifying: lambda === undefined ? undefined : lambda > 1,
    calibrated,
    // 诚实标注：接口返回 false 就显示「未校准」，绝不改写成「已校准」
    calibratedText: calibrated === undefined ? `${MISSING}标定状态` : calibrated ? '已标定' : UNCALIBRATED,
    calibratedTone: calibrated === undefined ? 'ghost' : calibrated ? 'ok' : 'warn',
    notice: typeof e.notice === 'string' ? e.notice : '',
  };
}

/**
 * 杠杆排序：按接口 `rows` 的顺序展示；**校验降序**。
 *
 * 接口已按增益降序返回，正常情况下 `reordered` 恒为 false。
 * 若某一版接口给出的顺序不是降序（例如按 H/E/W 固定顺序），这里按 gain 降序重排，
 * 并用 `reordered: true` 如实标记 —— 界面可以把这件事说出来，而不是悄悄改掉。
 */
export function toLeverageRows(leverage: SkLeverageResponse | undefined): SkLeverageView {
  const l = leverage ?? {};
  const rawRows = asArray(l.rows);

  const rows: SkLeverageRowView[] = rawRows.map((row) => {
    const key = isAxisKey(row?.key) ? row.key : undefined;
    const gain = num(row?.gain);
    const absorption = num(row?.absorption);
    return {
      key,
      name: typeof row?.name === 'string' && row.name.length > 0 ? row.name : key ?? MISSING,
      tone: key === undefined ? 'w' : TONES[key],
      order: 0,
      gain,
      gainDisplay: gain === undefined ? MISSING : `+${gain.toFixed(2)}`,
      finalComposite: num(row?.finalComposite),
      absorption,
      absorptionDisplay: absorption === undefined ? MISSING : `+${absorption.toFixed(2)}`,
      ratio: num(row?.ratio),
    };
  });

  // 降序校验：只在**有值**的项之间比较。
  // 缺失项不能参与比较，否则它会像通配符一样把两条本已乱序的记录「串」过去（漏报乱序）。
  const gains = rows.map((row) => row.gain).filter((g): g is number => g !== undefined);
  const inDescendingOrder = gains.every((g, i) => i === 0 || g <= gains[i - 1]);

  const reordered = !inDescendingOrder;
  if (reordered) {
    rows.sort((a, b) => {
      if (a.gain === undefined && b.gain === undefined) return 0;
      if (a.gain === undefined) return 1; // 缺失项排到最后，不参与排序结论
      if (b.gain === undefined) return -1;
      return b.gain - a.gain;
    });
  }
  rows.forEach((row, i) => {
    row.order = i + 1;
  });

  return {
    available: rows.length > 0,
    reordered,
    rows,
    conclusion: typeof l.conclusion === 'string' ? l.conclusion : '',
    calibrated: typeof l.calibrated === 'boolean' ? l.calibrated : undefined,
  };
}

/** 传导通道：把 `COUPLINGS` 按「从哪来 → 到哪去 / 强度 g / 时滞 lag / 依据 path」列表化。 */
export function toChannels(engine: SkEngineResponse | undefined, names: AxisNameTable): SkChannelView[] {
  return asArray(engine?.couplings).map((c) => {
    const from = typeof c?.from === 'string' && c.from.length > 0 ? c.from : MISSING;
    const to = typeof c?.to === 'string' && c.to.length > 0 ? c.to : MISSING;
    const g = num(c?.g);
    const nameOf = (axis: string): string => {
      const ch = axis.charAt(0).toUpperCase();
      return isAxisKey(ch) ? names[ch] : MISSING;
    };
    // 服务端为兼容小程序仍返回「资产健康」；H5 展示层恢复品牌词「财富健康」。
    const wealth = (value: string): string => value.replaceAll('资产健康', '财富健康');
    return {
      from,
      to,
      fromName: nameOf(from),
      toName: nameOf(to),
      g,
      gDisplay: g === undefined ? MISSING : g.toFixed(2),
      lag: typeof c?.lag === 'string' && c.lag.length > 0 ? c.lag : MISSING,
      path: typeof c?.path === 'string' && c.path.length > 0 ? wealth(c.path) : MISSING,
      name: typeof c?.name === 'string' && c.name.length > 0 ? wealth(c.name) : `${nameOf(from)} → ${nameOf(to)}`,
    };
  });
}

/** 取某轴在 `hist` 逐点矩阵里的轨迹。 */
function trajectoryOf(hist: number[][], index: number): number[] {
  const out: number[] = [];
  for (const point of hist) {
    const v = num(Array.isArray(point) ? point[index] : undefined);
    if (v !== undefined) out.push(v);
  }
  return out;
}

/**
 * 推演视图。
 *
 * 语义重点：`recovery` 是「首次三维同时回到个体化基准的月份」，**-1 表示窗口内未回稳**。
 * 因此 `recovered` 只在 `recovery >= 1` 时为 true —— 把 -1 映射成 false 而不是「不适用」，
 * 因为对成员而言「36 个月内没回到基线」正是最需要看见的那条结论。
 */
export function toSimulationView(sim: SkSimulateResponse | undefined, names: AxisNameTable): SkSimulationView {
  const s = sim ?? {};
  const without = s.withoutIntervention;
  const withI = s.withIntervention;
  const input = s.input ?? {};
  const months = num(input.months);

  const missing: string[] = [];
  if (!without) missing.push('withoutIntervention');
  if (!withI) missing.push('withIntervention');

  const recovery = num(without?.recovery);
  const recovered = recovery !== undefined && recovery >= 1;

  const axes: SkSimulationAxisView[] = AXIS_KEYS.map((key, i) => {
    const final = num(without?.final?.[i]);
    if (final === undefined) missing.push(`withoutIntervention.final.${key}`);
    const minRaw = Array.isArray(without?.minima) ? without.minima[i] : undefined;
    return {
      key,
      name: names[key],
      tone: TONES[key],
      final,
      finalDisplay: formatNumber(final),
      minimum: num(minRaw?.v),
      minimumMonth: num(minRaw?.m),
      recovered: recovery === undefined ? undefined : recovered,
      trajectory: trajectoryOf(asMatrix(without?.hist), i),
    };
  });

  // 未在窗口内回到基线的轴：三维同时回稳才叫回稳，因此这里三轴全列。
  // 拿不到 recovery 时返回空数组 —— 「不知道」不能渲染成「三轴都没回稳」。
  const unrecoveredKeys: string[] = recovery === undefined || recovered ? [] : AXIS_KEYS.slice();

  return {
    available: missing.length === 0,
    missing,
    months,
    recovered,
    recovery,
    unrecoveredKeys,
    withComposite: num(withI?.composite),
    withCompositeDisplay: formatNumber(withI?.composite),
    withoutComposite: num(without?.composite),
    withoutCompositeDisplay: formatNumber(without?.composite),
    withoutFinal: num(without?.final?.[0]),
    axes,
    lambda: num(s.lambda),
    baselineSource: typeof s.baselineSource === 'string' ? s.baselineSource : '',
    calibrated: typeof s.calibrated === 'boolean' ? s.calibrated : undefined,
    calibratedText: s.calibrated === undefined ? `${MISSING}标定状态` : s.calibrated ? '已标定' : UNCALIBRATED,
    notice: typeof s.notice === 'string' ? s.notice : '',
    disclaimer: '以上为情景推演，用于比较不同干预路径的相对效果，不是预测。',
    reason: without === undefined ? '接口未返回「不干预」推演结果' : '',
  };
}

/**
 * 基线来源 → 成员能看懂的话。
 * 服务端只回 `expert_prior` / `client_override` 两个值；其它值不猜，原样带出。
 */
export function baselineSourceText(source: string): string {
  if (source === 'expert_prior') return '个体化基准取专家先验值（尚未用真实队列数据标定）';
  if (source === 'client_override') return '个体化基准由本次推演参数指定';
  if (source === 'member_record') return '分值取成员存档的三康档案';
  return source.length > 0 ? source : MISSING;
}
